import { defineChain, getAddress, type Address } from 'viem'

/**
 * Everything this app reads from the environment, resolved once and validated here rather
 * than at the call sites.
 *
 * The two contract addresses are the interesting ones. They are needed *before* a quote
 * exists — the approval flow has to know who the Permit2 spender is, and the user must be
 * able to approve while the quote is still being shaped — so they cannot simply be read off
 * a `/swap` response. They are checked against that response instead, in `lib/addresses.ts`,
 * before anything is signed.
 */

function env(key: string): string | undefined {
  const value = import.meta.env[key as keyof ImportMetaEnv]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function address(key: string, fallback: string): Address {
  const raw = env(key) ?? fallback
  try {
    return getAddress(raw)
  } catch {
    throw new Error(`${key} is not a valid address: ${raw}`)
  }
}

export const FLARE_CHAIN_ID = 14

/**
 * Defined here rather than imported from `@sparkdex/shared`, which is a Node package: it
 * reaches for `node:fs` and `dotenv` through its own entry point and cannot be bundled for
 * a browser. The values below mirror `packages/shared/src/chain.ts` — change one, change
 * the other.
 */
export const flare = defineChain({
  id: FLARE_CHAIN_ID,
  name: 'Flare',
  nativeCurrency: { name: 'Flare', symbol: 'FLR', decimals: 18 },
  rpcUrls: {
    default: { http: [env('VITE_FLARE_RPC_URL') ?? 'https://flare-api.flare.network/ext/C/rpc'] },
  },
  blockExplorers: {
    default: { name: 'Flarescan', url: 'https://flarescan.com' },
  },
  contracts: {
    multicall3: { address: '0xcA11bde05977b3631167028862bE2a173976CA11' },
  },
})

export const config = {
  apiBaseUrl: (env('VITE_API_BASE_URL') ?? '/api').replace(/\/$/, ''),
  walletConnectProjectId: env('VITE_WALLETCONNECT_PROJECT_ID') ?? '',
  /** The OmegaRouter. Also the Permit2 spender every approval is granted to. */
  router: address('VITE_OMEGA_ROUTER_ADDRESS', '0x658B355320E75a063Bf1064a654D64e0693b31e7'),
  /** Flare's Permit2, which is NOT the canonical address. See docs/chain-facts.md. */
  permit2: address('VITE_PERMIT2_ADDRESS', '0xB952578f3520EE8Ea45b7914994dcf4702cEe578'),
} as const

/**
 * FLR held back from "Max" when paying in native FLR.
 *
 * FLR is the gas token, so spending the whole balance leaves nothing to pay for the swap and
 * the pre-signature `estimateGas` fails with an insufficient-funds error. An Algebra route
 * with the plugin's arbitrage cascade has been measured at ~2.4M gas; 1 FLR covers that up
 * to ~400 gwei, far above Flare's usual base fee, at a cost of a couple of cents.
 */
export const NATIVE_GAS_RESERVE = 10n ** 18n

/** Defaults for the two controls the user owns. Bounds come from the API, not from here. */
export const SWAP_DEFAULTS = {
  slippageBps: 50,
  deadlineMinutes: 5,
  /** `swap.minSlippageBps` / `maxSlippageBps` in api.config.json. Mirrored for the input. */
  minSlippageBps: 1,
  maxSlippageBps: 1_000,
  maxDeadlineSeconds: 1_800,
} as const

/**
 * How often a live quote is refreshed.
 *
 * Bounded from both sides. Below it, the API's quote cache (`cache.ttlMs`, 5s) serves the
 * same answer anyway and the per-client rate limit (5 rps sustained) is the only thing that
 * notices. Above it, the plan's signing window (`routeId.ttlMs`, 30s from when the plan was
 * *quoted*) starts expiring under the user's cursor. 10s leaves two thirds of the window.
 */
export const QUOTE_REFRESH_MS = 10_000
